import { formatLocalDate } from '../utils/localDateTime';
// components/ThreatGroupBriefing.tsx
import React, { useState, useEffect } from 'react';
import type { AnalysisOrPlanResult, CountryRiskProfileResult, EpOpsAnalysisResult, GeopoliticalAnalysisResult, GeopoliticalGovernmentAnalysisResult, GlobalThreatGroupAnalysisResult, HSSEComplianceBriefing, HSSEIncidentReport, TextAnalysisResult, ThreatGroupScores, Page, NavParams } from '@types';
import { ICONS, RISK_LEVEL_COLORS } from './constants';
import { createSafeMarkdown } from '../utils/safeMarkdown';
import RiskChart from './RiskChart';
import { printRenderedReport } from '../utils/printRenderedReport';
import Loader from './Loader';
import UnifiedMap from './UnifiedMap';
import { geocode } from '../../services/mapboxService';
import ApiErrorDisplay from './ApiErrorDisplay';
import AnalysisMarkdown from './reports/AnalysisMarkdown';
import ThreatGroupSupplement from './reports/ThreatGroupSupplement';
// G4 FIX: Import type guard to render context-appropriate header for HSSE Incident Reports
import { isHSSEIncident as isHSSEIncidentHeader } from '../utils/analysisTypeGuards';
import { getMapCoordinates } from '../utils/mapCoordinates';
import { threatGroupMapDetails } from '../utils/threatGroupMapDetails';
import useGoogleMaps from './useGoogleMaps';
import { prepareThreatGroupReport, threatGroupSections } from '../../functions/src/common/threatGroupReportText';

const md = createSafeMarkdown();

// --- Reusable Components for Modern Report Structure ---
const Section: React.FC<{ title: string; children: React.ReactNode; className?: string; id?: string }> = ({ title, children, className = '', id }) => (
    <section id={id} className={`report-print-section mb-6 ${className}`}>
        <h2 className="text-lg font-bold text-slate-800 border-b border-slate-200 pb-2 mb-4">{title}</h2>
        {children}
    </section>
);

const DefList: React.FC<{ items: { term: string; definition: React.ReactNode }[]; twoColumn?: boolean }> = ({ items, twoColumn = false }) => (
    <dl className={twoColumn ? "grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4" : "space-y-4"}>
        {items.map(item => (
            <div key={item.term} className="break-inside-avoid">
                <dt className="text-sm font-semibold text-slate-600">{item.term}</dt>
                <dd className="text-sm text-slate-800 mt-1 prose prose-sm max-w-none">{item.definition}</dd>
            </div>
        ))}
    </dl>
);


// Type Guards to identify the specific report type
const isTextResult = (result: AnalysisOrPlanResult): result is TextAnalysisResult => result !== null && 'summary' in result && 'sections' in result && (!result.reportType || result.reportType === 'TextAnalysisResult');
const isCountryRiskProfileResult = (result: AnalysisOrPlanResult): result is CountryRiskProfileResult => result !== null && 'reportType' in result && result.reportType === 'CountryRiskProfile';
const isGeopoliticalAnalysisResult = (result: AnalysisOrPlanResult): result is GeopoliticalAnalysisResult => result !== null && 'reportType' in result && result.reportType === 'GeopoliticalAnalysis';
const isGeopoliticalGovernmentAnalysisResult = (result: AnalysisOrPlanResult): result is GeopoliticalGovernmentAnalysisResult => result !== null && 'reportType' in result && result.reportType === 'GeopoliticalGovernmentAnalysis';
const isGlobalThreatGroupAnalysisResult = (result: AnalysisOrPlanResult): result is GlobalThreatGroupAnalysisResult => result !== null && 'reportType' in result && result.reportType === 'GlobalThreatGroupAnalysis';
const isHSSEComplianceBriefing = (result: AnalysisOrPlanResult): result is HSSEComplianceBriefing => result !== null && 'reportType' in result && result.reportType === 'HSSEComplianceBriefing';
const isHSSEIncidentReport = (result: AnalysisOrPlanResult): result is HSSEIncidentReport => result !== null && 'reportType' in result && result.reportType === 'HSSEIncidentReport';
const isEpOpsAnalysisResult = (result: AnalysisOrPlanResult): result is EpOpsAnalysisResult => result !== null && 'reportType' in result && (result as any).reportType === 'EpOpsAnalysisResult';

const ThreatGroupBriefing: React.FC<{ 
    result: AnalysisOrPlanResult, 
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void,
    onSaveToLibrary?: () => void,
    isSaving?: boolean
}> = ({ result, onNavigate, onSaveToLibrary, isSaving }) => {
    const [mapImageUrl, setMapImageUrl] = useState<string | null>(null);

    const handleDownload = async () => {
        let title = "Intelligence Report";
        if (result) {
            if (isGlobalThreatGroupAnalysisResult(result)) {
                title = `Intelligence Briefing: ${result.groupName}`;
            } else if (isGeopoliticalGovernmentAnalysisResult(result)) {
                title = `Geopolitical Government Risk: ${result.country}`;
            } else if (isEpOpsAnalysisResult(result)) {
                title = `Operations Risk Analysis: ${result.country}`;
            } else if (isCountryRiskProfileResult(result)) {
                title = `Travel Advisory: ${result.country}`;
            } else if (isHSSEComplianceBriefing(result)) {
                title = `HSSE Compliance Briefing: ${result.state}`;
            } else if (isHSSEIncidentReport(result)) {
                title = `HSSE Incident Report: ${result.caseNumber || 'Draft'}`;
            } else if ('title' in result && result.title) {
                title = result.title;
            }
        }
        printRenderedReport(title);
    };

    if (!result) return null;

    // --- Data Extraction ---
    let title = "Intelligence Report";
    let mainContent: React.ReactNode = null;

    if (isGlobalThreatGroupAnalysisResult(result)) {
        title = `Intelligence Briefing: ${result.groupName}`;
        mainContent = <GlobalThreatGroupContent analysis={result} onNavigate={onNavigate} />;
    } else if (isGeopoliticalGovernmentAnalysisResult(result)) {
        title = `Geopolitical Government Risk: ${result.country}`;
        mainContent = <GeopoliticalGovernmentContent result={result} />;
    } else if (isEpOpsAnalysisResult(result)) {
        title = `Security & EP Operations Risk Analysis: ${result.country}`;
        mainContent = <EpOpsAnalysisContent result={result} />;
    } else if (isCountryRiskProfileResult(result)) {
        title = `Travel Advisory: ${result.country}`;
        mainContent = <CountryRiskProfileContent result={result} />;
    } else if (isGeopoliticalAnalysisResult(result)) {
        title = result.title || title;
        mainContent = <GeopoliticalAnalysisContent result={result} />;
    } else if (isHSSEComplianceBriefing(result)) {
        title = `HSSE Compliance Briefing: ${result.state}`;
        mainContent = <HSSEComplianceContent result={result} />;
    } else if (isHSSEIncidentReport(result)) {
        title = `Draft HSSE Incident Report`;
        mainContent = <HSSEIncidentContent result={result} />;
    } else if (isTextResult(result)) {
        title = result.title || title;
        mainContent = <TextResultContent result={result} />;
    }

    return (
        <div className="analysis-report-print-area bg-white p-6 h-full flex flex-col font-sans">
             <div className="flex justify-between items-center border-b border-slate-200 pb-4 mb-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
                    {/* G4 FIX: Incident reports show operational metadata, not intelligence classification */}
                    {isHSSEIncidentHeader(result) ? (
                        <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs font-semibold text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">
                                {(result as any).status || 'DRAFT'}
                            </span>
                            {(result as any).caseNumber && (
                                <span className="text-sm text-slate-500">Case: {(result as any).caseNumber}</span>
                            )}
                            <span className="text-sm text-slate-400">Report Date: {formatLocalDate(new Date())}</span>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-500">Classification: Unrestricted // Generated: {formatLocalDate(new Date())}</p>
                    )}
                </div>
                <div className="flex space-x-2">
                    <button 
                        onClick={handleDownload}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md flex items-center transition-colors text-sm"
                        title="Download Report"
                    >
                        {React.cloneElement(ICONS.download as any, {className: 'h-4 w-4 mr-2'})}
                        <span>Print / Save PDF</span>
                    </button>
                    {onSaveToLibrary && (
                        <button 
                            onClick={onSaveToLibrary}
                            disabled={isSaving}
                            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold py-2 px-4 rounded-md flex items-center transition-colors text-sm"
                            title="Save to Library Vault"
                        >
                            {isSaving ? (
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                            ) : (
                                React.cloneElement(ICONS.save as any, {className: 'h-4 w-4 mr-2'})
                            )}
                            <span>{isSaving ? "Saving..." : "Save to Library"}</span>
                        </button>
                    )}
                </div>
            </div>
            
            <div className="flex-1 overflow-y-auto pr-2">
                {mainContent}
            </div>
        </div>
    );
};


// --- Specific Content Renderers ---

const EpOpsAnalysisContent: React.FC<{ result: EpOpsAnalysisResult }> = ({ result }) => {
    const toArray = (value: any): any[] => Array.isArray(value) ? value : (value ? [value] : []);
    
    const renderChecklistItem = (item: any) => {
        if (typeof item === 'object' && item !== null) {
            if (item.assessment) return `${item.type || 'Task'}: ${item.assessment}` + (item.locations ? ` (${item.locations.join(', ')})` : '');
            if (item.name && item.location && item.description) return `${item.name} (${item.location}): ${item.description}`;
            return JSON.stringify(item);
        }
        return item;
    };

    const threats = toArray(result.threatsToPersonnel);
    const considerations = toArray(result.logisticalConsiderations);
    const checklist = toArray(result.operationalChecklist);
    const plans = toArray(result.executionPlans);
    
    return (
        <div className="space-y-4">
            <Section title="Tactical Summary">
                <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: md.render(result.tacticalSummary || '') }} />
            </Section>
            <Section title="Threats to Personnel">
                {threats.map((item: any, index: number) => (
                    <div key={index} className="activity">
                        <DefList items={[
                            { term: 'Threat', definition: item.threat },
                            { term: 'Mitigation', definition: item.mitigation }
                        ]} />
                    </div>
                ))}
            </Section>
            <Section title="Logistical Considerations">
                {considerations.map((item, index) => (
                    <div key={index} className="activity">
                        <DefList items={[
                            { term: 'Area', definition: item.area },
                            { term: 'Recommendation', definition: item.recommendation }
                        ]} />
                    </div>
                ))}
            </Section>
            <Section title="Medical Assessment">
                 <DefList items={[
                    { term: 'Quality of Care', definition: result.medicalAssessment.qualityOfCare },
                    { term: 'Emergency Contacts', definition: result.medicalAssessment.emergencyContacts },
                    { term: 'Recommended Facilities', definition: result.medicalAssessment.recommendedFacilities },
                ]} twoColumn />
            </Section>
            <Section title="Law Enforcement">
                 <DefList items={[
                    { term: 'Disposition', definition: result.lawEnforcement.disposition },
                    { term: 'Corruption Level', definition: result.lawEnforcement.corruptionLevel },
                    { term: 'Contact Info', definition: result.lawEnforcement.contactInfo },
                ]} twoColumn />
            </Section>
            <Section title="Safe Havens & Evacuation">
                 <DefList items={[
                    { term: 'Safe Havens', definition: result.safeHavensAndEvacuation.safeHavens },
                    { term: 'Primary Evacuation Routes', definition: result.safeHavensAndEvacuation.primaryEvacRoutes },
                ]} />
            </Section>
            <Section title="Operational Checklist">
                <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">{checklist.map((item, index) => <li key={index}>{renderChecklistItem(item)}</li>)}</ul>
            </Section>
             <Section title="Execution Plans">
                {plans.map((plan, index) => (
                    <div key={index} className="activity">
                        <h4 className="font-semibold text-slate-800">{plan.teamComposition}</h4>
                        <div className="prose prose-sm max-w-none mt-2" dangerouslySetInnerHTML={{ __html: md.render(plan.plan || '') }} />
                    </div>
                ))}
            </Section>
            <Section title="Personnel Requirements">
                 <div className="activity">
                    <DefList items={[
                        { term: 'Required Experience', definition: <div dangerouslySetInnerHTML={{ __html: md.render(result.personnelRequirements?.experience || '')}}/> },
                        { term: 'Required Training', definition: <div dangerouslySetInnerHTML={{ __html: md.render(result.personnelRequirements?.training || '')}}/> }
                    ]} />
                </div>
            </Section>
            <Section title="Support Considerations">
                 <div className="activity">
                     <DefList items={[
                        { term: 'Equipment', definition: <div dangerouslySetInnerHTML={{ __html: md.render(result.supportConsiderations?.equipment || '')}}/> },
                        { term: 'Logistics', definition: <div dangerouslySetInnerHTML={{ __html: md.render(result.supportConsiderations?.logistics || '')}}/> },
                        { term: 'Administration', definition: <div dangerouslySetInnerHTML={{ __html: md.render(result.supportConsiderations?.admin || '')}}/> }
                    ]} />
                </div>
            </Section>
        </div>
    );
};

const GlobalThreatGroupContent: React.FC<{ analysis: GlobalThreatGroupAnalysisResult, onNavigate?: (nav: { page: Page; params?: NavParams }) => void }> = ({ analysis, onNavigate }) => {
    analysis = prepareThreatGroupReport(analysis);
    const { isLoaded } = useGoogleMaps();
    const riskColor = RISK_LEVEL_COLORS[analysis.threatLevel] || RISK_LEVEL_COLORS.Medium;
    const legacyAnalysis = analysis as any;
    const pmesiiAnalysis = analysis.pmesiiAnalysis || legacyAnalysis.pmesii;
    const carverAssessment = analysis.carverAssessment || legacyAnalysis.carver;
    const aoo = analysis.areaOfOperations;
    const validLocations: Array<{ lat: number; lng: number; name?: string } & ThreatGroupScores> = (aoo?.locations || legacyAnalysis.heatmapPoints || [])
        .flatMap((location: any) => {
            const coordinates = getMapCoordinates(location);
            return coordinates ? [{ ...location, ...coordinates }] : [];
        });
    const mapMarkers = (analysis.mapMarkers || legacyAnalysis.tacticalMarkers || [])
        .flatMap((marker: any) => {
            const coordinates = getMapCoordinates(marker);
            return coordinates ? [{ ...marker, ...coordinates }] : [];
        });
    const activityAlerts = (analysis.recentActivities || [])
        .flatMap((activity: any, index: number) => {
            const coordinates = getMapCoordinates(activity);
            return coordinates ? [{
                ...activity,
                ...threatGroupMapDetails(activity),
                ...coordinates,
                id: `act-${index}`,
                title: activity.type || 'Kinetic Event',
                summary: activity.description || 'Event telemetry recorded in area of operations.',
                severity: 'High',
                sentiment: -1,
                geo: { ...activity.geo, ...coordinates, location_name: activity.location || 'Unknown' }
            }] : [];
        });
    const aooCoordinates = getMapCoordinates(aoo);
    const mapCenter = aooCoordinates
        ? aooCoordinates
        : validLocations.length
            ? { lat: validLocations[0].lat, lng: validLocations[0].lng }
            : mapMarkers.length
                ? { lat: mapMarkers[0].lat, lng: mapMarkers[0].lng }
                : activityAlerts.length ? activityAlerts[0].geo : null;


    return (
        <div className="threat-group-report space-y-4">
            <div className={`p-4 rounded-lg border ${riskColor.bg} ${riskColor.border}`}>
                 <DefList items={[
                    { term: 'Threat Level', definition: <span className={`font-bold ${riskColor.text}`}>{analysis.threatLevel}</span> },
                    { term: 'Primary Country of Operation', definition: analysis.countryOfOperation }
                 ]} twoColumn />
            </div>
            <Section id={threatGroupSections.summary.id} title="Executive Summary">
                <AnalysisMarkdown value={analysis.summary || legacyAnalysis.description} className="text-sm text-slate-700 leading-relaxed" />
            </Section>
            <Section id={threatGroupSections.profile.id} title="Group Profile & Network">
                <DefList items={[
                    { term: 'Ideology and objectives', definition: <AnalysisMarkdown value={analysis.ideology} /> },
                    { term: 'Operational history', definition: <AnalysisMarkdown value={analysis.historicalContext} /> }
                ]} twoColumn />
                {!!analysis.keyLeaders?.length && (
                    <div id={threatGroupSections.leaders.id} className="mt-4">
                        <h3 className="font-semibold">Verified leadership</h3>
                        {analysis.keyLeaders.map((leader, index) => (
                            <div key={index} className="mt-2 text-sm">
                                <p><strong>{leader.name}</strong> — {leader.role}</p>
                                <AnalysisMarkdown value={leader.bio} />
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            {analysis.assetRiskAnalysis && (
                <Section id={threatGroupSections.asset.id} title="Asset-Specific Risk Assessment" className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <AnalysisMarkdown value={analysis.assetRiskAnalysis} className="text-slate-700 font-medium italic" />
                </Section>
            )}

             <Section title="Tactical Visualization & Asset Proximity" className="report-map-threat-section">
                <div className="report-map-threat-layout grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="report-map-threat-map lg:col-span-2 w-full h-[600px] rounded-md overflow-hidden border border-slate-300 bg-slate-950">
                        {mapCenter ? (
                            <UnifiedMap 
                            isLoaded={isLoaded}
                            initialViewState={{ 
                                longitude: mapCenter.lng,
                                latitude: mapCenter.lat,
                                zoom: aoo?.zoom || 5
                            }} 
                            theme="dark" 
                            sidebarTitle={`THREAT ACTIVITY: ${analysis.groupName}`}
                            heatmapPoints={validLocations?.map(loc => ({
                                lat: loc.lat,
                                lng: loc.lng,
                                weight: 2, 
                                radius: 45 
                            }))}
                            onNavigate={onNavigate}
                            activeThreatGroup={{
                                name: analysis.groupName || 'Unknown Threat Group',
                                threatLevel: analysis.threatLevel || 'Medium',
                                summary: analysis.summary || ''
                            }}
                            assets={analysis.nearbyAssets?.map(asset => ({
                                 ...asset,
                                 isAsset: true,
                                 criticality: asset.criticality || 'Medium'
                            }))}
                            markers={[
                                ...(validLocations?.map(loc => ({
                                    ...loc,
                                    ...threatGroupMapDetails(loc),
                                    isPrimary: loc.name === 'PROJECT ASSET',
                                    title: loc.name || 'Operational Area',
                                    type: loc.name === 'PROJECT ASSET' ? 'Asset' : 'ThreatGroup'
                                })) || []),
                                ...(mapMarkers.map((m: any) => ({
                                    ...m,
                                    ...threatGroupMapDetails(m),
                                    lat: m.lat,
                                    lng: m.lng,
                                    title: m.name,
                                    description: m.description,
                                    type: m.type
                                })) || [])
                            ]}
                            alerts={activityAlerts}
                            interactive={true}
                        />
                        ) : ( <div className="flex items-center justify-center h-full bg-slate-100 text-slate-500">Map data not available. Generating live view...</div> )}
                    </div>
                    
                    <div className="report-map-threat-list lg:col-span-1 flex flex-col h-[600px] border border-slate-300 rounded-md bg-slate-50 overflow-hidden shadow-inner">
                        <div className="p-3 bg-slate-200 border-b border-slate-300 font-bold text-xs uppercase tracking-wider text-slate-700 flex justify-between items-center">
                            <span>Kinetic Telemetry</span>
                            <span className="text-slate-500 bg-slate-300 px-2 rounded-full">{(analysis.recentActivities || []).length}</span>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {(analysis.recentActivities || []).length > 0 ? (
                                analysis.recentActivities!.map((activity: any, index: number) => (
                                    <div key={index} className="text-sm bg-white p-4 rounded-lg shadow-sm border-l-4 border-l-red-500 border-t border-r border-b border-slate-200 hover:shadow-md transition-shadow">
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="font-bold text-red-700 text-xs uppercase tracking-wide">{activity.type || 'Kinetic Event'}</span>
                                            <span className="text-[10px] text-slate-500 font-semibold bg-slate-100 px-2 py-0.5 rounded">{formatLocalDate(activity.date, activity.date || '')}</span>
                                        </div>
                                        <AnalysisMarkdown value={activity.description} className="text-slate-700 text-xs leading-relaxed" />
                                    </div>
                                ))
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full space-y-2 opacity-50">
                                    <p className="text-slate-500 text-sm italic font-medium">Monitoring feed optimal.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Section>

            {pmesiiAnalysis && (
                <Section id={threatGroupSections.pmesii.id} title="Intelligence Analysis (PMESII-PT)">
                    <DefList items={[
                        { term: 'Political', definition: <AnalysisMarkdown value={pmesiiAnalysis.political} /> },
                        { term: 'Military', definition: <AnalysisMarkdown value={pmesiiAnalysis.military} /> },
                        { term: 'Economic', definition: <AnalysisMarkdown value={pmesiiAnalysis.economic} /> },
                        { term: 'Social', definition: <AnalysisMarkdown value={pmesiiAnalysis.social} /> },
                        { term: 'Infrastructure', definition: <AnalysisMarkdown value={pmesiiAnalysis.infrastructure} /> },
                        { term: 'Information', definition: <AnalysisMarkdown value={pmesiiAnalysis.information} /> },
                        { term: 'Physical Environment', definition: <AnalysisMarkdown value={pmesiiAnalysis.physicalEnvironment} /> },
                        { term: 'Time', definition: <AnalysisMarkdown value={pmesiiAnalysis.time} /> },
                    ]} twoColumn />
                </Section>
            )}

            {analysis.ascopeAnalysis && (
                <Section id={threatGroupSections.ascope.id} title="Civil Considerations (ASCOPE)">
                    <DefList items={[
                        { term: 'Areas', definition: <AnalysisMarkdown value={analysis.ascopeAnalysis.areas} /> },
                        { term: 'Structures', definition: <AnalysisMarkdown value={analysis.ascopeAnalysis.structures} /> },
                        { term: 'Capabilities', definition: <AnalysisMarkdown value={analysis.ascopeAnalysis.capabilities} /> },
                        { term: 'Organizations', definition: <AnalysisMarkdown value={analysis.ascopeAnalysis.organizations} /> },
                        { term: 'People', definition: <AnalysisMarkdown value={analysis.ascopeAnalysis.people} /> },
                        { term: 'Events', definition: <AnalysisMarkdown value={analysis.ascopeAnalysis.events} /> },
                    ]} twoColumn />
                </Section>
            )}

            {carverAssessment && (
                 <Section id={threatGroupSections.carver.id} title="Target Vulnerability Assessment (CARVER)">
                    <DefList items={[
                        { term: 'Criticality', definition: <AnalysisMarkdown value={carverAssessment.criticality} /> },
                        { term: 'Accessibility', definition: <AnalysisMarkdown value={carverAssessment.accessibility} /> },
                        { term: 'Recuperability', definition: <AnalysisMarkdown value={carverAssessment.recuperability} /> },
                        { term: 'Vulnerability', definition: <AnalysisMarkdown value={carverAssessment.vulnerability} /> },
                        { term: 'Effect', definition: <AnalysisMarkdown value={carverAssessment.effect} /> },
                        { term: 'Recognizability', definition: <AnalysisMarkdown value={carverAssessment.recognizability} /> },
                    ]} twoColumn />
                     <div className="mt-4">
                        <h3 className="text-sm font-semibold text-slate-600">Overall Assessment</h3>
                        <AnalysisMarkdown value={carverAssessment.overallAssessment || carverAssessment.analysis} className="text-sm text-slate-800 mt-1" />
                    </div>
                </Section>
            )}

            <ThreatGroupSupplement result={analysis} />
        </div>
    );
};

const GeopoliticalGovernmentContent: React.FC<{ result: GeopoliticalGovernmentAnalysisResult; }> = ({ result }) => {
    const riskColor = RISK_LEVEL_COLORS[result.overallRiskLevel] || RISK_LEVEL_COLORS.Medium;
    const [center, setCenter] = useState<{lat: number; lng: number} | null>(null);
    const [isMapLoading, setIsMapLoading] = useState(true);
    const [mapError, setMapError] = useState<string | null>(null);

    useEffect(() => {
        const fetchCoords = async () => {
            if (result.country) {
                setIsMapLoading(true); setMapError(null);
                try {
                    const coords = await geocode(result.country);
                    setCenter(coords);
                } catch (error: any) {
                    setMapError(error.message); setCenter(null);
                } finally {
                    setIsMapLoading(false);
                }
            }
        };
        fetchCoords();
    }, [result.country]);
    
    const disclaimer = "DISCLAIMER: This report is generated based on Open-Source Intelligence (OSINT) gathered and analyzed by an AI model. Information is derived from publicly available sources at the time of generation and has not been independently verified by human analysts. This document is for informational and strategic planning purposes only and should not be considered as a definitive intelligence product or the sole basis for decision-making.";

    return (
        <div className="space-y-4">
            <div className={`p-4 rounded-lg border ${riskColor.bg} ${riskColor.border}`}>
                <DefList items={[
                     { term: 'Overall Risk Level', definition: <span className={`font-bold ${riskColor.text}`}>{result.overallRiskLevel}</span> },
                     ...(result.authoritarianismIndex ? [{ term: 'Authoritarianism Index', definition: `${result.authoritarianismIndex.classification} (${result.authoritarianismIndex.score}/10)` }] : [])
                ]} twoColumn />
            </div>
            <Section title="Executive Summary">
                <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: md.render(result.riskSummary || '') }} />
            </Section>
             <Section title="Geographical Overview">
                <div className="w-full h-96 rounded-md overflow-hidden border border-slate-300">
                    {isMapLoading ? <div className="h-full flex items-center justify-center bg-slate-100"><Loader /></div>
                    : mapError ? <div className="h-full flex items-center justify-center p-4"><ApiErrorDisplay error={mapError} serviceName="Insights Geocoding Service" /></div>
                    : center ? 
                    
                    <UnifiedMap 
                        isLoaded={!isMapLoading} // ADD THIS: Clears Error 2741
                        initialViewState={{ 
                            longitude: center.lng, 
                            latitude: center.lat, 
                            zoom: 4 
                        }} 
                        theme="light" 
                        alerts={[]} 
                    />                   
                : <p className="p-4 text-sm text-slate-500">Map could not be generated.</p>}
                </div>
            </Section>
            {result.riskMatrix && result.riskMatrix.length > 0 && (
                <Section title="Risk Matrix Visualization">
                    <div className="h-96 w-full"><RiskChart data={result.riskMatrix} /></div>
                </Section>
            )}
             {result.keyLeaders && result.keyLeaders.length > 0 && (
                 <Section title="Key Leaders">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
                        {(result.keyLeaders || []).map(leader => (
                            <div key={leader.name} className="activity text-center">
                                <div className="w-24 h-24 rounded-full mx-auto mb-3 bg-slate-200 overflow-hidden flex items-center justify-center">
                                    <div className="text-xs text-slate-400">Image N/A</div>
                                </div>
                                <h4 className="font-bold text-slate-800">{leader.name}</h4>
                                <p className="text-sm text-slate-500">{leader.role}</p>
                                <p className="text-xs text-slate-600 mt-2 text-justify">{leader.bio}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}
            <Section title="Detailed Analysis">
                {(result.detailedAnalysis || []).map((section, index) => (
                    <div key={index} className="mb-4">
                        <h3 className="font-bold text-slate-800 text-base">{section.title}</h3>
                        <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: md.render(section.content || '') }}/>
                    </div>
                ))}
            </Section>
            <Section title="Predictive Analysis">
                <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: md.render(result.predictiveAnalysis || '') }} />
            </Section>
             <Section title="OSINT Disclaimer" className="mt-8 pt-4 border-t-2 border-slate-300">
                <p className="text-xs text-slate-500 italic">{disclaimer}</p>
            </Section>
        </div>
    );
};

const GenericReportContent: React.FC<{ summary: string, sections: Array<{ title?: string; heading?: string; content: string }>, riskFactors?: any[] }> = ({ summary, sections, riskFactors }) => (
    <div className="space-y-4">
        <Section title="Executive Summary">
            <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: md.render(summary || '') }} />
        </Section>
        {(sections || []).map((section, index) => (
            <Section key={index} title={section.title || section.heading || `Section ${index + 2}`}>
                <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: md.render(section.content || '') }}/>
            </Section>
        ))}
        {riskFactors && riskFactors.length > 0 && (
            <Section title="Risk Factor Analysis">
                <div className="h-80"><RiskChart data={riskFactors} /></div>
            </Section>
        )}
    </div>
);

const CountryRiskProfileContent: React.FC<{ result: CountryRiskProfileResult }> = ({ result }) => (
    <GenericReportContent summary={result.advisorySummary} sections={result.sections} />
);

const GeopoliticalAnalysisContent: React.FC<{ result: GeopoliticalAnalysisResult }> = ({ result }) => (
    <GenericReportContent summary={result.summary || ''} sections={result.sections || []} />
);

const TextResultContent: React.FC<{ result: TextAnalysisResult }> = ({ result }) => (
    <GenericReportContent summary={result.summary || ''} sections={result.sections || []} riskFactors={result.riskFactors} />
);

const HSSEComplianceContent: React.FC<{ result: HSSEComplianceBriefing }> = ({ result }) => {
    const sections = [
        { title: "Regulatory Bodies", content: (result.regulatoryBodies || []).map(b => `* **${b.name}:** ${b.description}`).join('\n') },
        { title: "Key Regulations", content: (result.keyRegulations || []).map(r => `* **${r.regulation}:** ${r.summary}`).join('\n') },
        { title: "Compliance Checklist", content: (result.complianceChecklist || []).map(i => `* **${i.item}:** ${i.description}`).join('\n') },
    ];
    return <GenericReportContent summary={`Operational Context: ${result.operationalContext}`} sections={sections} />;
};

const HSSEIncidentContent: React.FC<{ result: HSSEIncidentReport }> = ({ result }) => {
    const items = [
        { term: 'Case Number', definition: result.caseNumber },
        { term: 'Employee Name', definition: result.employeeName },
        { term: 'Job Title', definition: result.jobTitle },
        { term: 'Date of Injury', definition: formatLocalDate(result.dateOfInjury, result.dateOfInjury) },
        { term: 'Time of Injury', definition: result.timeOfInjury },
        { term: 'Location of Incident', definition: result.locationOfIncident },
        { term: 'Description of Injury', definition: result.descriptionOfInjury },
        { term: 'Object/Substance', definition: result.objectOrSubstance },
    ];
    return (
        <div>
            <Section title="Incident Summary">
                <div className="prose prose-sm max-w-none">{result.incidentDescription}</div>
            </Section>
            <Section title="Incident Details">
                <DefList items={items} twoColumn />
            </Section>
        </div>
    );
};

export default ThreatGroupBriefing;
